import { motion } from "framer-motion";
import { MapPin, BedDouble, Bath, Ruler, Check, Download, Sparkles } from "lucide-react";
import { useLead } from "./LeadModal";
import { Tilt } from "./Reveal";
import { waLink } from "../lib/site";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16 2.7C8.7 2.7 2.8 8.6 2.8 15.9c0 2.6.8 5 2.1 7L2.7 29.3l6.6-2.1c1.9 1.2 4.2 1.9 6.7 1.9 7.3 0 13.2-5.9 13.2-13.2S23.3 2.7 16 2.7zm6.3 15.4c-.3.8-1.6 1.5-2.3 1.6-.6.1-1.4.2-2.2-.1-.5-.2-1.2-.4-2-.7-3.5-1.5-5.8-5.1-6-5.3-.1-.2-1.4-1.9-1.4-3.6s.8-2.6 1.2-2.9c.4-.4.8-.5 1-.5h.7c.2 0 .5-.1.8.6.3.7 1 2.5 1.1 2.7.1.2.1.4 0 .6-.1.2-.1.4-.3.6l-.6.7c-.2.2-.4.4-.2.7.2.3.9 1.5 1.9 2.4 1.3 1.2 2.5 1.5 2.8 1.7.4.2.6.1.8-.1.2-.2.9-1 1.1-1.3.2-.4.5-.3.8-.2.4.2 2.1 1 2.4 1.1.4.2.6.2.7.4.1.1.1.8-.2 1.6z" />
  </svg>
);

const goldBtn =
  "flex flex-1 items-center justify-center gap-2 bg-gold-500 px-4 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-gold-400 hover:shadow-[0_12px_24px_rgb(197,160,89,0.4)]";
const outlineBtn =
  "flex flex-1 items-center justify-center gap-2 border border-navy-900/25 px-4 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-navy-900 hover:bg-navy-900 hover:text-white";
const waBtn =
  "flex flex-1 items-center justify-center gap-2 bg-[#25D366] px-4 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgb(37,211,102,0.4)]";

const Spec = ({ icon: Icon, label, tid }) => (
  <span className="flex items-center gap-1.5 text-[13px] text-gray-600" data-testid={tid}>
    <Icon className="h-4 w-4 text-gold-600" />
    {label}
  </span>
);

export const ListingCard = ({ listing, index = 0, featured = false }) => {
  const { openLead } = useLead();
  const waText = `Hi Estate Paradise, I'm interested in ${listing.title} (${listing.location})${listing.price_text ? ` listed at ${listing.price_text}` : ""}${listing.rent ? ` at ₹${listing.rent.toLocaleString("en-IN")}/month` : ""}. Please share details.`;

  const imageBlock = (ratio = "aspect-[4/3]") => (
    <div className={`relative overflow-hidden ${ratio}`}>
      <img
        src={listing.image}
        alt={listing.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-950/45 via-transparent to-transparent opacity-80" />
      {listing.tag && (
        <span className="absolute left-4 top-4 flex items-center gap-1.5 bg-gold-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-navy-900" data-testid={`listing-tag-${listing.id}`}>
          <Sparkles className="h-3 w-3" />
          {listing.tag}
        </span>
      )}
    </div>
  );

  const locationRow = (
    <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-gray-500">
      <MapPin className="h-3.5 w-3.5 text-gold-600" />
      {listing.location}
    </p>
  );

  const card = (children, ratio) => (
    <Tilt className="h-full" max={4}>
      <motion.article
        initial={{ opacity: 0, y: 40, rotateX: 10, transformPerspective: 1100 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.85, delay: (index % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="group flex h-full flex-col border border-navy-900/10 bg-white shadow-[0_8px_30px_rgb(13,27,62,0.05)] transition-[box-shadow,border-color] duration-500 hover:border-gold-500/50 hover:shadow-[0_28px_56px_rgb(13,27,62,0.14)]"
        data-testid={`listing-card-${listing.id}`}
      >
        {imageBlock(ratio)}
        <div className="flex flex-1 flex-col p-6">{children}</div>
      </motion.article>
    </Tilt>
  );

  if (listing.category === "prelaunch") {
    return card(
      <>
        {listing.developer && (
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold-600">{listing.developer}</p>
        )}
        <h3 className="mt-1 font-serif text-2xl font-medium text-navy-900">{listing.title}</h3>
        {locationRow}
        <ul className="mt-4 space-y-2">
          {(listing.highlights || []).slice(0, 4).map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] leading-snug text-gray-600">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold-600" />
              {h}
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-5">
          <div className="mb-4 flex items-baseline justify-between border-t border-navy-900/10 pt-4">
            <span className="text-[11px] uppercase tracking-[0.18em] text-gray-400">{listing.config}</span>
            <span className="font-serif text-lg font-medium text-navy-900" data-testid={`listing-price-${listing.id}`}>
              {listing.price_text || "Price on Request"}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              data-testid={`brochure-btn-${listing.id}`}
              onClick={() => openLead({ title: listing.brochure_path ? "Download Brochure" : "Get Brochure & Price List", project: listing.title, intent: "brochure", interest: "Pre-Launch Investment", source: "Brochure Download", brochureListingId: listing.brochure_path ? listing.id : "" })}
              className={outlineBtn}
            >
              <Download className="h-4 w-4" />
              Brochure
            </button>
            <button
              data-testid={`inquire-btn-${listing.id}`}
              onClick={() => openLead({ title: "Inquire Now", project: listing.title, interest: "Pre-Launch Investment", source: "Project Inquiry" })}
              className={goldBtn}
            >
              Inquire Now
            </button>
          </div>
        </div>
      </>,
      featured ? "aspect-[16/10]" : "aspect-[4/3]"
    );
  }

  if (listing.category === "rental") {
    return card(
      <>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif text-xl font-medium text-navy-900">{listing.title}</h3>
            {locationRow}
          </div>
          <span className="shrink-0 border border-gold-500/50 bg-gold-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold-700">
            {listing.furnishing}
          </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          {listing.bhk && <Spec icon={BedDouble} label={`${listing.bhk} BHK`} tid={`spec-bhk-${listing.id}`} />}
          {listing.baths && <Spec icon={Bath} label={`${listing.baths} Bath`} tid={`spec-bath-${listing.id}`} />}
          {listing.area && <Spec icon={Ruler} label={`${listing.area} sq.ft`} tid={`spec-area-${listing.id}`} />}
        </div>
        <ul className="mt-4 space-y-2">
          {(listing.highlights || []).slice(0, 3).map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] leading-snug text-gray-600">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold-600" />
              {h}
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-5">
          <div className="mb-4 border-t border-navy-900/10 pt-4">
            <span className="font-serif text-2xl font-medium text-navy-900" data-testid={`listing-rent-${listing.id}`}>
              ₹{(listing.rent || 0).toLocaleString("en-IN")}
              <span className="text-sm text-gray-400"> / month</span>
            </span>
            {listing.maintenance && <p className="mt-1 text-[12px] text-gray-500">{listing.maintenance}</p>}
          </div>
          <div className="flex gap-3">
            <a href={waLink(waText)} target="_blank" rel="noopener noreferrer" className={waBtn} data-testid={`wa-btn-${listing.id}`}>
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp
            </a>
            <button
              data-testid={`inquire-btn-${listing.id}`}
              onClick={() => openLead({ title: "Move-In Enquiry", project: listing.title, interest: "Rental Property", source: "Rental Inquiry", rental: true })}
              className={outlineBtn}
            >
              Enquire
            </button>
          </div>
        </div>
      </>
    );
  }

  return card(
    <>
      <h3 className="font-serif text-xl font-medium text-navy-900">{listing.title}</h3>
      {locationRow}
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
        {listing.bhk && <Spec icon={BedDouble} label={`${listing.bhk} BHK`} tid={`spec-bhk-${listing.id}`} />}
        {listing.baths && <Spec icon={Bath} label={`${listing.baths} Bath`} tid={`spec-bath-${listing.id}`} />}
        {listing.area && <Spec icon={Ruler} label={`${listing.area} sq.ft`} tid={`spec-area-${listing.id}`} />}
      </div>
      <ul className="mt-4 space-y-2">
        {(listing.highlights || []).slice(0, 3).map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] leading-snug text-gray-600">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold-600" />
            {h}
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-5">
        <div className="mb-4 flex items-baseline justify-between border-t border-navy-900/10 pt-4">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-400">{listing.config}</span>
          <span className="font-serif text-xl font-medium text-navy-900" data-testid={`listing-price-${listing.id}`}>
            {listing.price_text}
          </span>
        </div>
        <div className="flex gap-3">
          <a href={waLink(waText)} target="_blank" rel="noopener noreferrer" className={waBtn} data-testid={`wa-btn-${listing.id}`}>
            <WhatsAppIcon className="h-4 w-4" />
            WhatsApp
          </a>
          <button
            data-testid={`inquire-btn-${listing.id}`}
            onClick={() => openLead({ title: "Inquire Now", project: listing.title, interest: "Buy / Resale Flat", source: "Resale Inquiry" })}
            className={outlineBtn}
          >
            Enquire
          </button>
        </div>
      </div>
    </>
  );
};
